import React, { useEffect } from 'react';

import { ToolHead } from '../../components/toolHead';

import studio from '../../assets/studio.svg';
import admin from '../../assets/admin.svg';
import insights from '../../assets/insights.svg';
import gsqlshell from '../../assets/gsqlshell.svg';
import graphql from '../../assets/graphql.svg';
import { useLicense } from '../../license/license';
import { getMainDomain, getTGUsername } from '../../utils/utils';
import clsx from 'clsx';

const StyledAppList = (props: React.HTMLProps<HTMLDivElement>) => {
  return (
    <div
      {...props}
      className={clsx(
        props.className,
        'max-w-[1200px] mx-auto pb-[20px] grid gap-[48px] grid-cols-[repeat(auto-fill,minmax(520px,1fr))] items-center justify-items-center justify-center max-[720px]:w-full'
      )}
    >
      {props.children}
    </div>
  );
};

function AppBox({ disabled, children, ...props }: { disabled?: boolean } & React.ComponentProps<'a'>) {
  return (
    <div className={clsx(disabled ? 'cursor-not-allowed' : 'cursor-pointer')}>
      <a
        {...props}
        className={clsx([
          disabled ? 'text-gray-300' : 'text-[#FFFFFF]',
          disabled ? 'pointer-events-none' : 'pointer-events-auto',
          'rounded-[10px] px-9 flex items-center box-border relative overflow-hidden w-[520px] h-[150px] no-underline border border-[#D4DADF] hover:box-shadow-[2px_2px_30px_rgba(25,118,210,0.05),_-2px_-2px_30px_rgba(25,118,210,0.05)] max-[720px]:w-[80%]',
        ])}
      >
        {children}
      </a>
    </div>
  );
}

const StyledAppIMG = (props: React.HTMLProps<HTMLDivElement>) => {
  return (
    <div {...props} className={clsx(props.className, 'mr-[16px] w-[184px] h-[96px] flex items-center')}>
      {props.children}
    </div>
  );
};

const StyledBoxinfo = (props: React.HTMLProps<HTMLParagraphElement>) => {
  return (
    <p
      {...props}
      className={clsx(props.className, 'font-[Urbanist] not-italic font-normal text-base leading-4 text-[#767676]')}
    >
      {props.children}
    </p>
  );
};

export default function AllApplication() {
  const username = getTGUsername();

  const { license } = useLicense();

  useEffect(() => {
    const { pathname } = window.location;
    const isMlwbRoute = pathname.startsWith('/mlwb');
    // The front-end of mlwb uses the front-end resources of the instance gui
    if (process.env.REACT_APP_ENV === 'cloud' && isMlwbRoute) {
      window.location.href = `/#/login?returnURL=${encodeURIComponent('/mlwb')}`;
      return;
    }
    if (process.env.REACT_APP_ENV === 'cloud') {
      window.location.href = `https://${getMainDomain(window.location.href)}`;
    }
  }, []);

  if (process.env.REACT_APP_ENV === 'cloud') {
    return null;
  }

  return (
    <>
      <div className="w-full">
        <ToolHead appname="TigerGraph Suite" appicon="/suittitle.svg" innerapp="home" user={username} />
      </div>
      <p className="py-[60px] font-[Urbanist] font-bold  text-[32px] leading-10 text-center capitalize text-[#294560]">
        What do you want to work on today?
      </p>
      <StyledAppList>
        <AppBox href="/studio" disabled={license.disableGraphStudio}>
          <StyledAppIMG>
            <img className="w-[184px] h-[96px]" src={studio} alt="" />
          </StyledAppIMG>
          <StyledBoxinfo>Design, develop, map and load your graph database.</StyledBoxinfo>
        </AppBox>

        <AppBox href="/insights" disabled={license.disableInsights}>
          <StyledAppIMG>
            <img className="w-[184px] h-[96px]" src={insights} alt="" />
          </StyledAppIMG>
          <StyledBoxinfo>Build graph powered applications in minutes.</StyledBoxinfo>
        </AppBox>

        <AppBox href="/gsql" disabled={license.disableGSQLShell}>
          <StyledAppIMG>
            <img className="w-[184px] h-[96px]" src={gsqlshell} alt="" />
          </StyledAppIMG>
          <StyledBoxinfo>Run your GSQL and visualize it.</StyledBoxinfo>
        </AppBox>
        <AppBox href="/admin">
          <StyledAppIMG>
            <img className="w-[184px] h-[96px]" src={admin} alt="" />
          </StyledAppIMG>
          <StyledBoxinfo>Manage your TigerGraph system.</StyledBoxinfo>
        </AppBox>
        <AppBox href="/graphql" disabled={license.disableGraphQL}>
          <StyledAppIMG>
            <img className="w-[184px] h-[96px]" src={graphql} alt="" />
          </StyledAppIMG>
          <StyledBoxinfo>Query and update your graph data at ease via GraphQL API.</StyledBoxinfo>
        </AppBox>
      </StyledAppList>
    </>
  );
}
